import React from 'react'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import { 
  selectWork,
  type WorkItem,
  type WorkRuntime
} from '../store/slices/workSlice'
import { workService } from '../services/workService'
import { formatDistanceToNow } from 'date-fns'
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  FileIcon,
  Loader2Icon,
  FunnelIcon
} from 'lucide-react'

type MergedWork = WorkItem & { runtime?: WorkRuntime }

export const ActivityCenter: React.FC = () => {
  const dispatch = useAppDispatch()
  const allWork = useAppSelector(state => {
    const { byId, ids } = state.work
    return ids.map(id => byId[id])
  })
  const selectedWorkId = useAppSelector(state => state.work.selectedWorkId)
  
  const handleSelectWork = (workId: string) => {
    dispatch(selectWork(workId === selectedWorkId ? undefined : workId))
  }
  
  // Group work items by status
  const processingWork = allWork.filter(w => w.status === 'running' || w.status === 'pending')
  const completedWork = allWork.filter(w => w.status === 'completed')
  const errorWork = allWork.filter(w => w.status === 'failed')
  
  return (
    <div className="activity-center h-full flex flex-col bg-white overflow-y-auto">
      {allWork.length === 0 ? (
        <div className="text-center py-8 text-gray-500 px-4">
          <FunnelIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No activity yet</p>
        </div>
      ) : (
        <div className="space-y-4 p-3">
          {/* Processing Section */}
          {processingWork.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                  Processing ({processingWork.length})
                </h4>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              {processingWork.map(work => (
                <WorkItemCard 
                  key={work.id} 
                  work={work}
                  selected={work.id === selectedWorkId}
                  onClick={() => handleSelectWork(work.id)}
                />
              ))}
            </div>
          )}
          
          {/* Complete Section */}
          {completedWork.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <h4 className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                  Complete ({completedWork.length})
                </h4>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              {completedWork.map(work => (
                <WorkItemCard 
                  key={work.id} 
                  work={work}
                  selected={work.id === selectedWorkId}
                  onClick={() => handleSelectWork(work.id)}
                />
              ))}
            </div>
          )}
          
          {/* Error Section */}
          {errorWork.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wide">
                  Error ({errorWork.length})
                </h4>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              {errorWork.map(work => (
                <WorkItemCard 
                  key={work.id} 
                  work={work}
                  selected={work.id === selectedWorkId}
                  onClick={() => handleSelectWork(work.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Work item card component
const WorkItemCard: React.FC<{
  work: MergedWork
  selected: boolean
  onClick: () => void
}> = ({ work, selected, onClick }) => {
  const { runtime } = work
  const isProcessing = work.status === 'running' || work.status === 'pending'
  
  const statusIcon = {
    pending: <ClockIcon className="w-5 h-5 text-gray-400" />,
    running: <Loader2Icon className="w-5 h-5 text-blue-500 animate-spin" />,
    completed: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
    failed: <XCircleIcon className="w-5 h-5 text-red-500" />,
    cancelled: <XCircleIcon className="w-5 h-5 text-gray-400" />
  }[work.status]
  
  const statusColor = {
    pending: 'text-gray-600',
    running: 'text-blue-600',
    completed: 'text-green-600',
    failed: 'text-red-600',
    cancelled: 'text-gray-500'
  }[work.status]
  
  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (work.uid && isProcessing) {
      await workService.cancelWork(work.id, work.uid)
    }
  }
  
  return (
    <div 
      className={`
        work-item-card rounded-lg border p-3 cursor-pointer transition-all
        ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}
      `}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        {statusIcon}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {work.subject.fileName || 'Unnamed Document'}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {work.subject.fileSize && formatFileSize(work.subject.fileSize)}
          </div>
        </div>
      </div>
      
      {/* Progress bar for running tasks */}
      {runtime && isProcessing && (
        <div className="mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-600 truncate flex-1">
              {runtime.message || 'Processing...'}
            </span>
            <span className="text-xs font-medium text-blue-600 ml-2">
              {runtime.progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className="bg-blue-500 h-1 rounded-full transition-all duration-300"
              style={{ width: `${runtime.progress}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Error message */}
      {work.error && (
        <div className="mb-2 p-2 bg-red-50 rounded text-xs text-red-700">
          {work.error.message}
        </div>
      )}
      
      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
        <span className="truncate">
          {formatDistanceToNow(new Date(work.createdAt), { addSuffix: true })}
        </span>
        {isProcessing && (
          <button
            onClick={handleCancel}
            className="text-red-600 hover:text-red-700 font-medium ml-2 flex-shrink-0"
          >
            Cancel
          </button>
        )}
        {work.completedAt && (
          <span className="ml-2 flex-shrink-0">
            {formatDuration(work.completedAt - work.createdAt)}
          </span>
        )}
      </div>
    </div>
  )
}

// Helper functions
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

export default ActivityCenter
